import { ReactNode, useEffect } from "react";
import {
  FieldLabel,
  Field,
  FieldContent,
  FieldTitle,
  FieldDescription,
} from "./ui/Field";
import { RadioGroup, RadioGroupItem } from "./ui/RadioGroup";
import { useAtom } from "jotai";
import { paymentOptionAtom } from "./store";
import { GetBookingQueryResult } from "@repo/rq-api-public";
import { formatCurrency } from "../utils/currency";

export enum PaymentOption {
  Full = "full",
  Deposit = "deposit",
}

export default function PaymentOptionRadioGroup({
  booking,
  currency,
  locale,
}: {
  booking: GetBookingQueryResult;
  currency: string;
  locale: string;
}) {
  const [paymentOption, setPaymentOption] = useAtom(paymentOptionAtom);
  const { prePayment } = booking;

  useEffect(
    () => {
      if (!booking.prePayment) setPaymentOption(PaymentOption.Full);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [booking]
  );

  if (!prePayment) return null;

  return (
    <RadioGroup
      value={paymentOption}
      onValueChange={(val) => setPaymentOption(val as PaymentOption)}
    >
      <PaymentOptionRadio
        value={PaymentOption.Full}
        title="Pay in full"
        description={
          <>
            {formatCurrency(booking.totalPrice ?? 0, currency, locale)} +
            processing fee
          </>
        }
      />
      <PaymentOptionRadio
        value={PaymentOption.Deposit}
        title={`${prePayment.percentage}% deposit`}
        description={
          <>
            {formatCurrency(prePayment.amount!, currency, locale)} now +
            processing fee, rest before trip
          </>
        }
      />
    </RadioGroup>
  );
}

export function PaymentOptionRadio({
  description,
  title,
  value,
}: {
  value: PaymentOption;
  title: ReactNode;
  description: ReactNode;
}) {
  const radioId = `payment-${value}`;

  return (
    <FieldLabel
      htmlFor={radioId}
      className="mt-m py-m px-l rounded-radius-l lg:mt-0 body-m cursor-pointer"
    >
      <Field orientation="horizontal" className=" items-center!">
        <RadioGroupItem value={value} id={radioId} className="text-blue-dark" />
        <FieldContent className="gap-2">
          <FieldTitle className="font-bold">{title}</FieldTitle>
          <FieldDescription className="text-text-secondary font-light">
            {description}
          </FieldDescription>
        </FieldContent>
      </Field>
    </FieldLabel>
  );
}
